import * as React from "react";
import { displayErrorToast } from "src/components/shared/Toasts/Toast";
import { ToastIds } from "src/enums/errorToastIds";
import { UserRole } from "src/enums/user/userRoles";
import { useGameContext } from "src/game/useGameContext";
import {
  TaskListItemUpperRowWrapper,
  TitleLabel,
  TaskButtonsWrapper,
  EditTaskIcon,
  DeleteTaskIcon,
  TaskListItemBottomRowWrapper,
  ResultContentWrapper,
  ResultLabel,
  ResultContent,
  ResultContentValue,
  VoteAgainWrapper,
  RepeatVoteIcon,
  RepeatVoteLabel,
  VoteTaskButton,
  TaskContentWrapper,
  CurrentlyVotingLabel,
  DeleteTaskConfirmationPopup,
} from "./TaskContentPanel.components";

export interface Props {
  taskId: string;
  title: string;
  finalResult: string | null;
  isDeletingTask: boolean;
  toggleDeleteConfirmPanel: (isDeleting: boolean, taskKey?: string) => void;
  toggleEditPanel: (isDeleting: boolean, taskKey?: string) => void;
}

export const TaskContentPanel: React.FunctionComponent<Props> = ({
  taskId,
  title,
  finalResult,
  toggleDeleteConfirmPanel,
  toggleEditPanel,
  isDeletingTask,
}) => {
  const { gameState, actionHandlers } = useGameContext();

  const wasVoted: boolean = finalResult != null;
  const isCurrentlyVotedOn: boolean = gameState.game?.task?.id === taskId;

  const handleVote = async () => {
    if (!gameState.game?.id || !gameState.token) {
      return;
    }

    const authData = {
      gameId: gameState.game?.id,
      token: gameState.token,
    };

    try {
      const response = await actionHandlers.startNewVoting({
        ...authData,
        taskId: taskId,
      });
      if (response.error) {
        displayErrorToast(
          `Failed to start a new vote ${response.error.message}`,
          ToastIds.NEW_VOTING_FAILED
        );
      }
    } catch (e) {
      displayErrorToast(
        `Failed to start a new vote ${e}`,
        ToastIds.NEW_VOTING_FAILED
      );
    }
  };

  const handleDeleteTask = async () => {
    if (!gameState.game?.id || !gameState.token) {
      return;
    }

    const authData = {
      gameId: gameState.game?.id,
      token: gameState.token,
    };

    try {
      const response = await actionHandlers.removeTask({
        task: {
          id: taskId,
        },
        ...authData,
      });
      if (response.error) {
        displayErrorToast(
          `Failed to remove task ${response.error.message}`,
          ToastIds.TASK_REMOVE_FAILED
        );
      } else {
        toggleDeleteConfirmPanel(false);
      }
    } catch (e) {
      displayErrorToast(
        "Failed to remove task${message}",
        ToastIds.TASK_REMOVE_FAILED
      );
    }
  };

  return (
    <TaskContentWrapper>
      <TaskListItemUpperRowWrapper>
        <TitleLabel>{title}</TitleLabel>
        <TaskButtonsWrapper isVisible={gameState.user?.role == UserRole.OWNER}>
          <EditTaskIcon
            data-testid="editTaskIcon"
            onClick={() => {
              toggleDeleteConfirmPanel(false);
              toggleEditPanel(true, taskId);
            }}
          />
          <DeleteTaskIcon
            data-testid="deleteTaskIcon"
            onClick={() => {
              toggleEditPanel(false);
              toggleDeleteConfirmPanel(true, taskId);
            }}
          />
        </TaskButtonsWrapper>
      </TaskListItemUpperRowWrapper>
      <TaskListItemBottomRowWrapper>
        <ResultContentWrapper>
          <ResultLabel>Final result:</ResultLabel>
          <ResultContent wasVoted={wasVoted}>
            <ResultContentValue>
              {wasVoted ? finalResult : "-"}
            </ResultContentValue>
          </ResultContent>
        </ResultContentWrapper>
        {isCurrentlyVotedOn ? (
          <CurrentlyVotingLabel>Voting ...</CurrentlyVotingLabel>
        ) : gameState.user?.role == UserRole.OWNER ? (
          wasVoted ? (
            <VoteAgainWrapper onClick={handleVote}>
              <RepeatVoteIcon />
              <RepeatVoteLabel>Vote again</RepeatVoteLabel>
            </VoteAgainWrapper>
          ) : (
            <VoteTaskButton onClick={handleVote}>Vote this task</VoteTaskButton>
          )
        ) : null}
      </TaskListItemBottomRowWrapper>
      {isDeletingTask && (
        <DeleteTaskConfirmationPopup
          onConfirm={handleDeleteTask}
          onCancel={() => {
            toggleDeleteConfirmPanel(false);
          }}
          confirmButtonLabel={"Delete"}
        />
      )}
    </TaskContentWrapper>
  );
};

export default TaskContentPanel;
